import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react'
import axios from '../../../../config/axiosConfig'
import { getMe } from '../../../../api/api'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {WithChildren} from '../../../../_metronic/helpers'


type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()

  useEffect(() => {
    if (auth?.api_token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${auth.api_token}`
    } else {
      delete axios.defaults.headers.common['Authorization']
    }
  }, [auth])

  const saveAuth = useCallback((nextAuth: AuthModel | undefined) => {
    setAuth(nextAuth)
    if (nextAuth) authHelper.setAuth(nextAuth)
    else authHelper.removeAuth()
  }, [])

  const logout = useCallback(async () => {
    try {
      await axios.post('/api/auth/logout')
    } catch (error) {
      console.error('Errore durante il logout', error)
    } finally {
      saveAuth(undefined)
      setCurrentUser(undefined)
    }
  }, [saveAuth])

  const value = useMemo(
    () => ({auth, saveAuth, currentUser, setCurrentUser, logout}),
    [auth, saveAuth, currentUser, logout]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  useEffect(() => {
    let cancelled = false;
  
    const requestUser = async () => {
      try {
        const me = await getMe();
        if (!cancelled) setCurrentUser(me);
      } catch (e) {
        if (!cancelled) logout();
      } finally {
        if (!cancelled) setShowSplashScreen(false);
      }
    };
  
    if (auth?.api_token) requestUser();
    else setShowSplashScreen(false);
  
    return () => { cancelled = true; };
  }, [auth, logout, setCurrentUser]);

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
